// Task A4: the shared platform × placement × mediaKind resolver — the SAME
// module the client preview/picker and the server dispatcher use (Decision
// #18, "single shared resolver ... no server hand-copy"). Explicit relative
// path + `.ts` extension (not the `@/` alias) so this file keeps working
// when Deno resolves it directly through `supabase/functions/_shared/
// posting-readiness.ts`'s re-export — this file already duplicates a few
// Instagram constants for the same Deno-import-boundary reason (see
// INSTAGRAM_REEL_MAX_SECONDS below).
import { toMatrixPlatform } from '../src/lib/platform-matrix/placement-matrix.ts';
import {
  type ConnectionAccountType,
  type MediaKind,
  PUBLISHABLE_PLACEMENTS,
  connectionPlacementDisabledReason,
  placementDisabledReason,
  resolvePlacement,
} from '../src/lib/platform-matrix/placement-resolution.ts';

// TikTok photo-mode task: same directory, plain relative import (both files
// live in `shared/`) — no Deno-import-boundary concern here.
import { FACEBOOK_MULTI_PHOTO_MAX } from './facebook-post-params.ts';
import { LINKEDIN_COMMENTARY_MAX_CHARS, linkedInCaptionLength } from './linkedin-little-text.ts';
import {
  FACEBOOK_LIMITS,
  INSTAGRAM_LIMITS,
  isNearVerticalRatio,
  ratioInWindow,
} from './meta-media-limits.ts';
import {
  LINKEDIN_IMAGE_FORMAT_MESSAGE,
  LINKEDIN_VIDEO_MAX_SECONDS,
  LINKEDIN_VIDEO_MIN_SECONDS,
  isLinkedInDocumentUrl,
  isLinkedInSupportedImageUrl,
} from './linkedin-media-rules.ts';
import {
  THREADS_CAROUSEL_MAX_CHILDREN,
  THREADS_TEXT_MAX_CHARS,
  THREADS_VIDEO_MAX_SECONDS,
  isThreadsSupportedImageUrl,
} from './threads-post-params.ts';
import { type TikTokCreatorInfo, tiktokMaxVideoSeconds } from './tiktok-creator-info.ts';
import {
  TIKTOK_PHOTO_DESCRIPTION_MAX_RUNES,
  TIKTOK_PHOTO_MAX_IMAGES,
  normalizeTikTokSettings,
} from './tiktok-post-params.ts';
import { type TikTokVideoFacts, evaluateTikTokVideoFacts } from './tiktok-video-facts.ts';

export type PostingPlatformId = 'instagram' | 'tiktok' | 'linkedin' | 'facebook' | string;

// `evaluatePlatformReadiness` lowercases the incoming platform key before it
// does anything else, so every message that interpolated it rendered
// "instagram is not connected for publishing." — six pixels from a chip that
// correctly read "Instagram". Capitalising HERE, at the message source, is
// what keeps the fix from being a per-call-site patch: the client chip, the
// footer's disabled-action title, the dispatcher logs and the edge-function
// re-export all read the same string. Only the brands whose casing is not a
// plain capitalise need an entry.
const PLATFORM_DISPLAY_NAMES: Record<string, string> = {
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
  twitter: 'X',
  google_business: 'Google Business',
  'google-business': 'Google Business',
};

export function platformDisplayName(platform: unknown): string {
  const key = String(platform ?? '')
    .toLowerCase()
    .trim();
  if (!key) return 'Platform';
  return PLATFORM_DISPLAY_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

export type PostingMediaType = 'image' | 'video' | 'carousel' | 'text';

export type PostingReadinessState =
  'ready' | 'warning' | 'blocked' | 'paused' | 'locked_live' | 'retrying' | 'terminal_failed';

export type PostingReadinessSeverity = 'warning' | 'blocked';

export type PostingReadinessDimension =
  'connection' | 'caption' | 'media' | 'schedule' | 'lifecycle' | 'transform' | 'governance';

export interface PostingReadinessIssue {
  code: string;
  message: string;
  severity: PostingReadinessSeverity;
  dimension: PostingReadinessDimension;
}

export interface PostingReadinessInput {
  platform: PostingPlatformId;
  caption?: string | null;
  mediaUrls?: string[] | null;
  mediaType?: PostingMediaType | string | null;
  connected?: boolean;
  disabled?: boolean;
  hasUnsavedCaption?: boolean;
  platformStatus?: string | null;
  rowStatus?: string | null;
  retryCount?: number | null;
  maxRetries?: number;
  videoDurationSeconds?: number | null;
  videoAspectRatio?: string | null;
  /**
   * Task A5: the CONNECTION's `user_connections.account_type` (raw, as
   * stored — case handled inside `connectionSupportsPlacement`). Consumed by
   * the connection-level placement gate below (e.g. Instagram Stories
   * require a Business/Creator account on the FB-Login flow). `null`/
   * `undefined`/unrecognized values are NOT assumed Business — fails CLOSED.
   */
  accountType?: ConnectionAccountType;
  /**
   * TikTok only: the connection's stored `creator_info` (privacy options,
   * longest video). When absent the checks that need it are skipped, never
   * guessed.
   */
  tiktokCreatorInfo?: TikTokCreatorInfo | null;
  /**
   * TikTok video only: size, frame rate and sound read from the file in the
   * editor. Absent on the server, where nothing probes the file.
   */
  tiktokVideoFacts?: TikTokVideoFacts | null;
  perPlatform?: {
    aspectRatio?: string | null;
    variantUrl?: string | null;
    transformFailed?: boolean | null;
    /** Task A4: stored placement override — see the placement-aware check
     * in `evaluatePlatformReadiness` below for how an invalid value here is
     * self-healed rather than trusted directly. */
    placement?: string | null;
    /** TikTok settings card values, as stored in `media_keys.perPlatform.tiktok`. */
    tiktok?: unknown;
  } | null;
}

export interface PostingReadinessResult {
  platform: PostingPlatformId;
  state: PostingReadinessState;
  canPublish: boolean;
  mediaType: PostingMediaType;
  issues: PostingReadinessIssue[];
  blockingIssues: PostingReadinessIssue[];
  warningIssues: PostingReadinessIssue[];
  primaryAction: string | null;
}

const VIDEO_URL_RE = /\.(mp4|mov|webm|m4v)(?:[?#].*)?$/i;
const DEFAULT_MAX_RETRIES = 3;
// Instagram and Facebook limits live in shared/meta-media-limits.ts, the single
// source readiness, platform-specs.ts and mediaValidation.ts all read.

function formatSecondsLimit(seconds: number): string {
  return seconds >= 120 && seconds % 60 === 0 ? `${seconds / 60} minutes` : `${seconds} seconds`;
}

function formatTikTokDuration(seconds: number): string {
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }
  return seconds > 60 ? `${Math.floor(seconds / 60)} min ${seconds % 60} s` : `${seconds} seconds`;
}

function parseAspectRatio(value: string): number | null {
  const parts = value.split(':').map((part) => Number(part.trim()));
  if (parts.length !== 2 || parts.some((part) => !Number.isFinite(part) || part <= 0)) {
    return null;
  }
  return parts[0] / parts[1];
}

const CAPTION_LIMITS: Record<string, number> = {
  instagram: 2200,
  tiktok: 2200,
  linkedin: 3000,
  facebook: 63206,
  // Threads is 500 — by far the tightest ceiling here, and the single
  // highest-value entry in this map. Without it `captionLimit` is `undefined`
  // and every over-long Threads caption passes readiness clean, then fails at
  // the API. Verified: developers.facebook.com/documentation/threads/posts.
  threads: THREADS_TEXT_MAX_CHARS,
};

function issue(
  code: string,
  message: string,
  severity: PostingReadinessSeverity,
  dimension: PostingReadinessDimension,
): PostingReadinessIssue {
  return { code, message, severity, dimension };
}

export function isPostingVideoUrl(url: string): boolean {
  return VIDEO_URL_RE.test(url);
}

export function normalizePostingMediaUrls(value: string[] | null | undefined): string[] {
  return Array.isArray(value)
    ? value.filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    : [];
}

export function inferPostingMediaType(
  explicit: PostingReadinessInput['mediaType'],
  mediaUrls: string[] | null | undefined,
): PostingMediaType {
  const urls = normalizePostingMediaUrls(mediaUrls);
  // A multi-URL post is a carousel even when a child is a video. Provider
  // readiness then applies each platform's mixed-media rule from the URLs;
  // collapsing the set to `video` here would make an Instagram mixed carousel
  // look like a single video and would drift from the dispatcher.
  if (urls.length > 1) return 'carousel';
  // A stale `media_type: image` must not route an obvious .mp4 to an image
  // publisher. Keep an explicit video label authoritative for extensionless
  // URLs, but let a recognizable video URL override a stale non-video label.
  if (explicit === 'video' || urls.some(isPostingVideoUrl)) return 'video';
  // A single media URL is also authoritative over stale text/carousel labels.
  // The URL set is the durable payload; the legacy enum is only a hint when
  // there is no media to inspect.
  if (urls.length === 1) return 'image';
  if (explicit === 'image' || explicit === 'carousel' || explicit === 'text') {
    return explicit;
  }
  if (urls.length === 0) return 'text';
  return 'text';
}

function lifecycleState(input: PostingReadinessInput): PostingReadinessState | null {
  const platformStatus = input.platformStatus ?? input.rowStatus ?? null;
  const retryCount = input.retryCount ?? 0;
  const maxRetries = input.maxRetries ?? DEFAULT_MAX_RETRIES;

  if (input.disabled) return 'paused';
  if (platformStatus === 'published') return 'locked_live';
  if (platformStatus === 'publishing') return 'retrying';
  if (platformStatus === 'scheduled' && retryCount > 0) return 'retrying';
  if (platformStatus === 'failed_connection_removed') return 'terminal_failed';
  if (platformStatus === 'failed' && retryCount >= maxRetries) return 'terminal_failed';
  if (input.rowStatus === 'failed' || input.rowStatus === 'failed_connection_removed') {
    return 'terminal_failed';
  }
  return null;
}

/**
 * Exported (not just used internally) so the exact-code→CTA mapping is
 * directly unit-testable — review finding P2 (readiness angle): the
 * `placement_media_mismatch` case is unreachable through
 * `evaluatePlatformReadiness` TODAY (every reachable placement/mediaKind
 * mismatch on a publishable platform is already deduped by an earlier
 * media-dimension code — see `post-readiness-placement.test.ts`), so testing
 * the mapping itself, independent of reachability, is the only way to pin
 * down its correctness ahead of Task A5/A6 making it reachable.
 */
export function primaryActionFor(result: {
  state: PostingReadinessState;
  blockingIssues: PostingReadinessIssue[];
  warningIssues: PostingReadinessIssue[];
}): string | null {
  if (result.state === 'paused') return 'Resume platform';
  if (result.state === 'locked_live') return 'View live';
  if (result.state === 'retrying') return 'Review retry';
  if (result.state === 'terminal_failed') return 'Retry send';
  const firstBlock = result.blockingIssues[0];
  if (!firstBlock) return result.warningIssues[0] ? 'Review warning' : null;
  switch (firstBlock.code) {
    case 'platform_not_connected':
      return 'Reconnect account';
    case 'caption_unsaved':
      return 'Save draft';
    case 'caption_too_long':
      return 'Fix caption';
    // placement_media_mismatch (review finding P2, readiness angle):
    // unreachable today (every reachable placement/mediaKind mismatch on a
    // publishable platform is already deduped by one of the codes below via
    // `alreadyBlockedForMedia` — see the placement-aware check further down
    // in this file), but once Task A5/A6 add narrower story/reel placements
    // this becomes reachable, and it's fundamentally the same "swap your
    // media" fix as the codes below — same CTA, not the generic fallback.
    // TikTok photo-mode task: `tiktok_media_required` renamed from
    // `tiktok_video_required` — TikTok now accepts image/carousel media too
    // (see the `tiktok` case below), so this code only fires for the true
    // "no media at all" case, same as the generic `media_required` it now
    // sits next to. The three photo-specific blocks (mixed media, >35
    // images, non-JPEG/WEBP format) are all the same "swap your media" fix.
    case 'placement_media_mismatch':
    case 'media_required':
    case 'tiktok_media_required':
    case 'tiktok_mixed_media_not_supported':
    case 'tiktok_too_many_photos':
    case 'tiktok_video_dimensions_unsupported':
    case 'tiktok_video_fps_unsupported':
    case 'document_not_supported':
    case 'linkedin_too_many_documents':
    case 'linkedin_too_many_videos':
    case 'linkedin_mixed_media_not_supported':
    case 'linkedin_video_too_short':
    case 'linkedin_video_too_long':
    case 'linkedin_image_format_unsupported':
    case 'facebook_mixed_media_not_supported':
    case 'facebook_reel_story_single_item':
    // Threads: all three are "swap your media" fixes. `threads_content_required`
    // is deliberately NOT here — with zero media the fix is to write text, not
    // replace media, so it falls through to the generic action.
    case 'threads_too_many_items':
    case 'threads_image_format_unsupported':
    case 'threads_video_too_long':
      return 'Replace media';
    // Task A5: the CONNECTION (not the media) is the blocker — the fix is
    // reconnecting as a Business/Creator account, same CTA as an expired/
    // revoked connection.
    case 'placement_requires_business_account':
      return 'Reconnect account';
    case 'tiktok_privacy_required':
    case 'tiktok_privacy_unavailable':
    case 'tiktok_commercial_choice_required':
    case 'tiktok_branded_content_private':
    case 'tiktok_consent_required':
      return 'Review TikTok settings';
    default:
      return 'Fix issue';
  }
}

/**
 * Whether a per-platform aspect override GUARANTEES this image set is
 * re-encoded as JPEG before the platform ever sees it.
 *
 * WHY THIS EXISTS. The two format rules below (`threads_image_format_
 * unsupported`, `tiktok_photo_format_unsupported`) test the SOURCE url's
 * extension. But the publish path re-encodes: `post-dispatcher.ts:394-417`
 * gates `transformMediaSetForPlatform` on `Boolean(perPlatformCfg?.aspectRatio)`,
 * `transform-media.ts:132` uploads the result with contentType 'image/jpeg',
 * and `buildVariantPath` (:259) emits a `.jpg` path — so with an override set,
 * the platform receives JPEG regardless of what the source url ends in.
 * `src/lib/content-quality/checks/format.ts:64` already knows this and passes
 * the quality chip; without the same knowledge here the chip and the readiness
 * sentence beside it contradicted each other on the exact case the chip's own
 * "Convert to JPEG" fix targets (`fixes.ts#setCropReencodeJpegFix`, which sets
 * nothing but an aspect ratio).
 *
 * DELIBERATELY CONSERVATIVE — every clause below is a case where the re-encode
 * is NOT guaranteed, so the source-extension test must still run:
 *  · no/unparseable ratio      → the dispatcher's `targetRatio` is undefined
 *                                and no transform runs at all.
 *  · `transformFailed`         → the dispatcher fell back to the source asset
 *                                (this also raises its own blocked issue below,
 *                                so the guard is belt-and-braces).
 *  · any video url in the set  → `hasVideoSource` makes the dispatcher skip the
 *                                transform (and hard-error on an explicit crop).
 *  · no media                  → nothing to re-encode.
 *
 * NOT checked here on purpose: `validateRatioForPlatform`'s accept window. It
 * lives in an edge-only module (`supabase/functions/_shared/transform-media.ts`)
 * that this browser-and-Deno-shared file cannot import, and an out-of-window
 * ratio makes the dispatcher throw a named, merchant-readable error rather than
 * silently publishing the un-re-encoded source. Every override Mora itself
 * writes comes from a placement row's `recommendedRatio`, which is in-window by
 * construction.
 */
function aspectOverrideReencodesToJpeg(input: PostingReadinessInput, mediaUrls: string[]): boolean {
  if (mediaUrls.length === 0) return false;
  if (input.perPlatform?.transformFailed === true) return false;
  const ratio = input.perPlatform?.aspectRatio;
  if (typeof ratio !== 'string' || parseAspectRatio(ratio.trim()) === null) return false;
  return !mediaUrls.some(isPostingVideoUrl);
}

export function evaluatePlatformReadiness(input: PostingReadinessInput): PostingReadinessResult {
  const platform = String(input.platform).toLowerCase();
  const caption = input.caption ?? '';
  const mediaUrls = normalizePostingMediaUrls(input.mediaUrls);
  const mediaType = inferPostingMediaType(input.mediaType, mediaUrls);
  const reencodesToJpeg = aspectOverrideReencodesToJpeg(input, mediaUrls);
  const issues: PostingReadinessIssue[] = [];
  const lifecycle = lifecycleState(input);

  if (lifecycle && lifecycle !== 'retrying') {
    const blockingIssues: PostingReadinessIssue[] = [];
    const warningIssues: PostingReadinessIssue[] = [];
    const result = {
      platform,
      state: lifecycle,
      canPublish: false,
      mediaType,
      issues,
      blockingIssues,
      warningIssues,
      primaryAction: null,
    };
    return { ...result, primaryAction: primaryActionFor(result) };
  }

  if (input.connected === false) {
    issues.push(
      issue(
        'platform_not_connected',
        `${platformDisplayName(platform)} is not connected for publishing.`,
        'blocked',
        'connection',
      ),
    );
  }

  if (input.hasUnsavedCaption) {
    issues.push(
      issue('caption_unsaved', 'Save the caption before publishing.', 'blocked', 'caption'),
    );
  }

  // A TikTok photo post keeps the whole caption as its description (4,000
  // characters); only a video caption stops at 2,200.
  const captionLimit =
    platform === 'tiktok' && (mediaType === 'image' || mediaType === 'carousel')
      ? TIKTOK_PHOTO_DESCRIPTION_MAX_RUNES
      : CAPTION_LIMITS[platform];
  if (captionLimit && caption.length > captionLimit) {
    issues.push(
      issue(
        'caption_too_long',
        `${platformDisplayName(platform)} captions support up to ${captionLimit.toLocaleString()} characters.`,
        'blocked',
        'caption',
      ),
    );
  }

  if (platform !== 'linkedin' && mediaUrls.some(isLinkedInDocumentUrl)) {
    issues.push(
      issue(
        'document_not_supported',
        `${platformDisplayName(platform)} cannot post documents. Remove the document, or post it to LinkedIn only.`,
        'blocked',
        'media',
      ),
    );
  }

  switch (platform) {
    case 'instagram':
      if (mediaUrls.length === 0) {
        issues.push(
          issue(
            'media_required',
            'Instagram requires image, video, or carousel media.',
            'blocked',
            'media',
          ),
        );
      }
      if (mediaUrls.length > INSTAGRAM_LIMITS.carouselMaxItems) {
        issues.push(
          issue(
            'instagram_carousel_too_many_items',
            `Instagram carousel posts support ${INSTAGRAM_LIMITS.carouselMinItems} to ${INSTAGRAM_LIMITS.carouselMaxItems} media items.`,
            'blocked',
            'media',
          ),
        );
      }
      if (mediaType === 'video') {
        // The limits belong to the RESOLVED placement, not to "video": a Story
        // is 3 to 60 s, a Reel is 3 s to 15 min. Both read shared/meta-media-limits.ts.
        const videoPlacement = resolvePlacement({
          platform,
          mediaKind: 'video',
          override: input.perPlatform?.placement ?? null,
        })?.placement;
        const isStory = videoPlacement === 'story';
        const limits = isStory ? INSTAGRAM_LIMITS.story : INSTAGRAM_LIMITS.reel;
        const noun = isStory ? 'Stories' : 'Reels';
        const codePrefix = isStory ? 'instagram_story' : 'instagram_reel';
        if (typeof input.videoDurationSeconds === 'number') {
          if (input.videoDurationSeconds > limits.maxSeconds) {
            issues.push(
              issue(
                `${codePrefix}_too_long`,
                `Instagram ${noun} must be ${formatSecondsLimit(limits.maxSeconds)} or shorter.`,
                'blocked',
                'media',
              ),
            );
          } else if (input.videoDurationSeconds < limits.minSeconds) {
            issues.push(
              issue(
                `${codePrefix}_too_short`,
                `Instagram ${noun} must be at least ${limits.minSeconds} seconds.`,
                'blocked',
                'media',
              ),
            );
          }
        }
        if (input.videoAspectRatio) {
          const ratio = parseAspectRatio(input.videoAspectRatio);
          if (ratio !== null) {
            if (!ratioInWindow(ratio, limits.ratio)) {
              issues.push(
                issue(
                  `${codePrefix}_aspect_ratio_unsupported`,
                  `Instagram ${noun} accept ratios from ${limits.ratio.min}:1 to ${limits.ratio.max}:1 (current: ${input.videoAspectRatio}).`,
                  'blocked',
                  'media',
                ),
              );
            } else if (!isNearVerticalRatio(ratio)) {
              issues.push(
                issue(
                  `${codePrefix}_aspect_ratio_not_vertical`,
                  `Instagram ${noun} look best at 9:16 (current: ${input.videoAspectRatio}). It will post, with bars or a crop.`,
                  'warning',
                  'media',
                ),
              );
            }
          }
        }
      }
      break;
    // TikTok photo-mode task (roadmap format-breadth item): the Content
    // Posting API's PHOTO Direct Post path (POST /v2/post/publish/content/
    // init/, PULL_FROM_URL, up to 35 images — shared/tiktok-post-params.ts)
    // is now live in post-to-tiktok, so image/carousel media is no longer
    // blocked here. Text-only (no media at all) is still blocked — TikTok
    // has no post_mode that publishes a caption with zero media.
    case 'tiktok':
      if (mediaUrls.length === 0) {
        issues.push(
          issue(
            'tiktok_media_required',
            'TikTok requires an image, photo carousel, or video.',
            'blocked',
            'media',
          ),
        );
      } else if (mediaType === 'video') {
        if (mediaUrls.length > 1) {
          issues.push(
            issue(
              'tiktok_single_video_only',
              'TikTok publishing in Mora currently sends one video at a time.',
              'blocked',
              'media',
            ),
          );
        }
        for (const fact of evaluateTikTokVideoFacts(input.tiktokVideoFacts)) {
          issues.push(issue(fact.code, fact.message, fact.severity, 'media'));
        }
        const tiktokMaxSeconds = tiktokMaxVideoSeconds(input.tiktokCreatorInfo);
        if (
          typeof input.videoDurationSeconds === 'number' &&
          input.videoDurationSeconds > tiktokMaxSeconds
        ) {
          issues.push(
            issue(
              'tiktok_video_too_long',
              input.tiktokCreatorInfo?.maxVideoPostDurationSec
                ? `This TikTok account can post videos up to ${formatTikTokDuration(tiktokMaxSeconds)}.`
                : `TikTok videos can be up to ${formatTikTokDuration(tiktokMaxSeconds)} long.`,
              'blocked',
              'media',
            ),
          );
        }
      } else if (mediaType === 'image' || mediaType === 'carousel') {
        // Review finding #2: re-derive from the URLs like the facebook
        // branch below — never trust the mediaType LABEL alone. schedule-post
        // patches a client-supplied media_type verbatim and both
        // inferPostingMediaType and the dispatcher's inferMediaType
        // short-circuit on an explicit value, so a stale/mismatched
        // 'image'/'carousel' label with a video URL mixed into mediaUrls
        // would otherwise sail through to TikTok's PHOTO endpoint as an
        // opaque TikTok-side rejection instead of Mora's own honest block.
        const hasVideoUrl = mediaUrls.some(isPostingVideoUrl);
        if (hasVideoUrl) {
          issues.push(
            issue(
              'tiktok_mixed_media_not_supported',
              'TikTok photo posts support images only. Use one video or image-only media.',
              'blocked',
              'media',
            ),
          );
        }
        if (mediaUrls.length > TIKTOK_PHOTO_MAX_IMAGES) {
          issues.push(
            issue(
              'tiktok_too_many_photos',
              `TikTok photo posts support up to ${TIKTOK_PHOTO_MAX_IMAGES} images.`,
              'blocked',
              'media',
            ),
          );
        }
        // Format is not a readiness problem: the TikTok publisher reads each
        // photo's real bytes and converts anything that is not a 1080 px JPEG
        // under 20 MB (`normalizeImageForPlatform` in the dispatcher).
      }
      // UNAUDITED-CLIENT CAVEAT (corrected per review finding #1, and
      // applied to EVERY TikTok media kind per finding #8 — the restriction
      // is client-wide, not photo-specific): TikTok REJECTS (403) an init
      // whose privacy_level is not among creator_info's privacy_level_
      // options — it never silently downgrades. post-to-tiktok now queries
      // creator_info and publishes at an OFFERED level, so for an unaudited
      // client the post publishes but may be limited to private (SELF_ONLY)
      // visibility until TikTok audit approval. No audit-status record for
      // Mora's TikTok app was found anywhere in this repo/docs, so this is
      // treated as UNAUDITED — non-blocking, but surfaced honestly.
      if (mediaUrls.length > 0) {
        const tiktokSettings = normalizeTikTokSettings(input.perPlatform?.tiktok);
        const sendsToDrafts = tiktokSettings?.deliveryMode === 'drafts';
        const creatorOptions = input.tiktokCreatorInfo?.privacyLevelOptions ?? [];
        if (!sendsToDrafts) {
          if (!tiktokSettings?.privacyLevel) {
            issues.push(
              issue(
                'tiktok_privacy_required',
                'Choose who can see this TikTok post.',
                'blocked',
                'governance',
              ),
            );
          } else if (
            creatorOptions.length > 0 &&
            !creatorOptions.includes(tiktokSettings.privacyLevel)
          ) {
            issues.push(
              issue(
                'tiktok_privacy_unavailable',
                'TikTok does not offer that audience for this account. Choose another.',
                'blocked',
                'governance',
              ),
            );
          }
          if (
            tiktokSettings?.commercialContent &&
            !tiktokSettings.yourBrand &&
            !tiktokSettings.brandedContent
          ) {
            issues.push(
              issue(
                'tiktok_commercial_choice_required',
                'You need to indicate if your content promotes yourself, a third party, or both.',
                'blocked',
                'governance',
              ),
            );
          }
          if (
            tiktokSettings?.commercialContent &&
            tiktokSettings.brandedContent &&
            tiktokSettings.privacyLevel === 'SELF_ONLY'
          ) {
            issues.push(
              issue(
                'tiktok_branded_content_private',
                'Branded content visibility cannot be set to private.',
                'blocked',
                'governance',
              ),
            );
          }
          if (tiktokSettings && !tiktokSettings.consent) {
            issues.push(
              issue(
                'tiktok_consent_required',
                'Confirm the TikTok terms under the settings before posting.',
                'blocked',
                'governance',
              ),
            );
          }
        }
        const offersPublicAudience = creatorOptions.some((option) => option !== 'SELF_ONLY');
        if (!sendsToDrafts && !offersPublicAudience) {
          issues.push(
            issue(
              'tiktok_unaudited_may_publish_private',
              "TikTok posts may be limited to private (SELF_ONLY) visibility until Mora's TikTok app passes TikTok's Content Posting API audit.",
              'warning',
              'governance',
            ),
          );
        }
      }
      break;
    case 'linkedin': {
      const length = linkedInCaptionLength(caption);
      if (length.level === 'escaped_over') {
        issues.push(
          issue(
            'linkedin_caption_escaped_near_limit',
            `LinkedIn receives this caption as ${length.escaped.toLocaleString()} characters, because it marks special characters and hashtags. If LinkedIn counts those, it will refuse the post. Shorten the caption a little to be safe.`,
            'warning',
            'caption',
          ),
        );
      } else if (length.level === 'near') {
        issues.push(
          issue(
            'linkedin_caption_near_limit',
            `This caption is ${length.raw.toLocaleString()} of ${LINKEDIN_COMMENTARY_MAX_CHARS.toLocaleString()} characters.`,
            'warning',
            'caption',
          ),
        );
      }
      const videoCount = mediaUrls.filter(isPostingVideoUrl).length;
      const documentCount = mediaUrls.filter(isLinkedInDocumentUrl).length;
      const hasVideo = videoCount > 0 || mediaType === 'video';
      if (documentCount > 1) {
        issues.push(
          issue(
            'linkedin_too_many_documents',
            'LinkedIn posts take one document. Keep one document, or use images only.',
            'blocked',
            'media',
          ),
        );
      } else if (documentCount === 1 && mediaUrls.length > 1) {
        issues.push(
          issue(
            'linkedin_mixed_media_not_supported',
            'LinkedIn cannot mix a document with images or a video in one post. Keep the document or the rest.',
            'blocked',
            'media',
          ),
        );
      }
      if (videoCount > 1) {
        issues.push(
          issue(
            'linkedin_too_many_videos',
            'LinkedIn posts take one video. Keep one video, or use images only.',
            'blocked',
            'media',
          ),
        );
      } else if (hasVideo && mediaUrls.length > 1 && documentCount === 0) {
        issues.push(
          issue(
            'linkedin_mixed_media_not_supported',
            'LinkedIn cannot mix a video with images in one post. Keep the video or the images.',
            'blocked',
            'media',
          ),
        );
      }
      const nonMp4 = mediaUrls.find(
        (url) => isPostingVideoUrl(url) && !/\.mp4(?:[?#].*)?$/i.test(url),
      );
      if (nonMp4) {
        issues.push(
          issue(
            'linkedin_video_not_mp4',
            'LinkedIn lists MP4 as the video format. This file may be refused or fail to process. Export it as an MP4 to be safe.',
            'warning',
            'media',
          ),
        );
      }
      if (hasVideo && typeof input.videoDurationSeconds === 'number') {
        if (input.videoDurationSeconds < LINKEDIN_VIDEO_MIN_SECONDS) {
          issues.push(
            issue(
              'linkedin_video_too_short',
              `LinkedIn videos must be at least ${LINKEDIN_VIDEO_MIN_SECONDS} seconds long.`,
              'blocked',
              'media',
            ),
          );
        } else if (input.videoDurationSeconds > LINKEDIN_VIDEO_MAX_SECONDS) {
          issues.push(
            issue(
              'linkedin_video_too_long',
              'LinkedIn videos must be 30 minutes or shorter.',
              'blocked',
              'media',
            ),
          );
        }
      }
      if (mediaUrls.length > 20) {
        issues.push(
          issue(
            'linkedin_too_many_images',
            'LinkedIn multi-image posts support up to 20 images.',
            'blocked',
            'media',
          ),
        );
      }
      if (
        mediaUrls.some(
          (url) =>
            !isPostingVideoUrl(url) &&
            !isLinkedInDocumentUrl(url) &&
            !isLinkedInSupportedImageUrl(url),
        )
      ) {
        issues.push(
          issue(
            'linkedin_image_format_unsupported',
            LINKEDIN_IMAGE_FORMAT_MESSAGE,
            'blocked',
            'media',
          ),
        );
      }
      break;
    }
    case 'facebook': {
      const hasVideo = mediaUrls.some(isPostingVideoUrl);
      if (mediaUrls.length > 1 && hasVideo) {
        issues.push(
          issue(
            'facebook_mixed_media_not_supported',
            'Facebook multi-media publishing in Mora supports image albums only. Use one video or image-only media.',
            'blocked',
            'media',
          ),
        );
      }
      // Reels and Page Stories are chosen placements (never the default for a
      // video). They publish one item, and Meta sets their video windows:
      // Reels 3 to 90 s at 9:16, Page stories 3 to 60 s.
      const facebookPlacement =
        mediaUrls.length > 0
          ? resolvePlacement({
              platform,
              mediaKind: mediaType as MediaKind,
              override: input.perPlatform?.placement ?? null,
            })?.placement
          : undefined;
      if (facebookPlacement === 'reel' || facebookPlacement === 'story') {
        const isReel = facebookPlacement === 'reel';
        const noun = isReel ? 'Reels' : 'Stories';
        const limits = isReel ? FACEBOOK_LIMITS.reel : FACEBOOK_LIMITS.story;
        if (mediaUrls.length > 1) {
          issues.push(
            issue(
              'facebook_reel_story_single_item',
              `Facebook ${noun} publish one item at a time.`,
              'blocked',
              'media',
            ),
          );
        }
        if (mediaType === 'video' && typeof input.videoDurationSeconds === 'number') {
          if (input.videoDurationSeconds > limits.maxSeconds) {
            issues.push(
              issue(
                `facebook_${facebookPlacement}_too_long`,
                `Facebook ${noun} must be ${limits.maxSeconds} seconds or shorter.`,
                'blocked',
                'media',
              ),
            );
          } else if (input.videoDurationSeconds < limits.minSeconds) {
            issues.push(
              issue(
                `facebook_${facebookPlacement}_too_short`,
                `Facebook ${noun} must be at least ${limits.minSeconds} seconds.`,
                'blocked',
                'media',
              ),
            );
          }
        }
        if (mediaType === 'video' && input.videoAspectRatio) {
          const ratio = parseAspectRatio(input.videoAspectRatio);
          if (ratio !== null && !isNearVerticalRatio(ratio)) {
            issues.push(
              issue(
                `facebook_${facebookPlacement}_aspect_ratio`,
                isReel
                  ? `Facebook Reels must be 9:16 (current: ${input.videoAspectRatio}).`
                  : `Facebook Stories look best at 9:16 (current: ${input.videoAspectRatio}). It will post, with bars or a crop.`,
                isReel ? 'blocked' : 'warning',
                'media',
              ),
            );
          }
        }
      }
      // Full citation trail + why this is a product guard, not a verbatim
      // Meta limit, in facebook-post-params.ts (Meta's own docs publish no
      // numeric cap for attached_media, unlike Instagram's documented-10
      // carousel and LinkedIn's documented 2-20 multiImage range).
      if (mediaUrls.length > FACEBOOK_MULTI_PHOTO_MAX) {
        issues.push(
          issue(
            'facebook_too_many_images',
            `Facebook multi-photo posts support up to ${FACEBOOK_MULTI_PHOTO_MAX} images.`,
            'blocked',
            'media',
          ),
        );
      }
      break;
    }
    // Threads. NOTE the deliberate absence of a `media_required` rule: Threads
    // is the ONLY platform Mora targets where a text-only post is valid
    // (media_type=TEXT), so requiring media here would block its one unique
    // capability. All ceilings verified against
    // developers.facebook.com/documentation/threads/posts (2026-07-25).
    case 'threads': {
      if (mediaUrls.length === 0 && caption.trim().length === 0) {
        issues.push(
          issue(
            'threads_content_required',
            'Threads posts need either text or media.',
            'blocked',
            'media',
          ),
        );
      }
      if (mediaUrls.length > THREADS_CAROUSEL_MAX_CHILDREN) {
        issues.push(
          issue(
            'threads_too_many_items',
            `Threads carousels support up to ${THREADS_CAROUSEL_MAX_CHILDREN} items.`,
            'blocked',
            'media',
          ),
        );
      }
      // 🔴 The high-value one. Threads accepts JPEG/PNG only, but Mora's
      // `imageCompression.ts` emits WebP by DEFAULT and the media library is
      // already full of `.webp` — so without this rule a Mora-generated image
      // sails through readiness and then fails at the Threads API.
      // OVERRIDE-AWARE: an aspect override re-encodes that same WebP to JPEG
      // before Threads sees it, so the source extension is not the question —
      // see `aspectOverrideReencodesToJpeg` above.
      const badThreadsImage =
        !reencodesToJpeg &&
        mediaUrls.some((url) => !isPostingVideoUrl(url) && !isThreadsSupportedImageUrl(url));
      if (badThreadsImage) {
        issues.push(
          issue(
            'threads_image_format_unsupported',
            'Threads accepts JPEG or PNG images only (not WebP).',
            'blocked',
            'media',
          ),
        );
      }
      if (
        mediaType === 'video' &&
        typeof input.videoDurationSeconds === 'number' &&
        input.videoDurationSeconds > THREADS_VIDEO_MAX_SECONDS
      ) {
        issues.push(
          issue(
            'threads_video_too_long',
            `Threads videos must be ${THREADS_VIDEO_MAX_SECONDS} seconds or shorter.`,
            'blocked',
            'media',
          ),
        );
      }
      break;
    }
    default:
      issues.push(
        issue(
          'platform_constraints_unverified',
          `${platformDisplayName(platform)} has no Mora readiness rule yet; the platform API may still reject this post.`,
          'warning',
          'governance',
        ),
      );
      break;
  }

  // Task A4: placement-aware readiness. The EFFECTIVE placement — override
  // from `perPlatform.placement` if `resolvePlacement` accepts it, else the
  // same legacy-parity derivation the client preview/dispatcher use — must
  // accept this post's mediaKind. A stale/invalid override (e.g. a 'feed'
  // placement recorded before Instagram video became Reels-only) is IGNORED
  // by `resolvePlacement` itself, so it can never spuriously block a publish
  // the resolver would happily redirect elsewhere — this check only ever
  // sees the SELF-HEALED placement, never the raw override.
  //
  // Scoped to platforms Mora can actually publish to today
  // (`PUBLISHABLE_PLACEMENTS[platform].length > 0`): for a preview-only
  // platform (x, youtube, pinterest, threads) `resolvePlacement`'s terminal
  // fallback returns the platform's primary row REGARDLESS of mediaKind by
  // design (legacy parity — see placement-resolution.ts), which for e.g.
  // YouTube (`short`, video-only) would otherwise turn every image post into
  // a brand-new block that never existed before this task.
  //
  // Deduped against the platform-specific switch above via
  // `alreadyBlockedForMedia`: today every reachable BLOCKING mismatch on a
  // publishable platform (tiktok+no-media, linkedin+video) is already
  // caught by an existing code (`tiktok_media_required`, `linkedin_video_
  // not_supported`) — this check adds NO new blocking issue for those, it
  // only activates for combinations the switch-case doesn't already reach
  // (e.g. once Task A5/A6 add story/reel placements with narrower media-kind
  // acceptance than 'feed'). TikTok photo-mode task: tiktok+image/carousel is
  // now a WARNING, not a blocked issue, so `alreadyBlockedForMedia` stays
  // false for it and this check correctly falls through to confirm the
  // resolved 'photo' placement accepts image/carousel too (it does — see
  // PLACEMENT_MEDIA_KINDS.photo in placement-resolution.ts).
  const matrixPlatform = toMatrixPlatform(platform);
  const isPublishablePlatform = matrixPlatform
    ? PUBLISHABLE_PLACEMENTS[matrixPlatform].length > 0
    : false;
  if (isPublishablePlatform) {
    const alreadyBlockedForMedia = issues.some(
      (item) => item.dimension === 'media' && item.severity === 'blocked',
    );
    if (!alreadyBlockedForMedia) {
      const effectivePlacement = resolvePlacement({
        platform,
        mediaKind: mediaType as MediaKind,
        override: input.perPlatform?.placement ?? null,
      });
      const placementReason = effectivePlacement
        ? placementDisabledReason(platform, effectivePlacement.placement, mediaType as MediaKind)
        : null;
      if (placementReason) {
        issues.push(issue('placement_media_mismatch', placementReason, 'blocked', 'media'));
      } else if (effectivePlacement) {
        // Task A5: media-kind is fine, but THIS connection may not be able
        // to publish to the resolved placement at all (e.g. Instagram
        // Stories require a Business/Creator account on the FB-Login flow).
        // `resolvePlacement` above only enforces the STATIC
        // `PUBLISHABLE_PLACEMENTS` ceiling — this is the narrower,
        // per-connection axis (`connectionSupportsPlacement`) that a client-
        // forged or stale override can't bypass, since it's re-derived
        // server-side from `input.accountType` (never trusted off the
        // client) exactly like `effectivePlacement` itself.
        const connectionReason = connectionPlacementDisabledReason(
          platform,
          effectivePlacement.placement,
          input.accountType,
        );
        if (connectionReason) {
          issues.push(
            issue('placement_requires_business_account', connectionReason, 'blocked', 'media'),
          );
        }
      }
    }
  }

  // No "media_transform_pending" warning here on purpose (removed, founder
  // review): the user chose a crop, and the dispatcher applying it before
  // publish (transformMediaSetForPlatform in post-dispatcher.ts) is expected
  // behaviour, not a problem — surfacing it as a `warning` in the same
  // channel as real blockers like "Instagram not connected" devalued the
  // channel by making a healthy state look unhealthy. It was also purely
  // transitional and self-resolving. A genuine failure of that same
  // transform still surfaces below via `media_transform_failed` (blocked) —
  // that branch is untouched.
  if (input.perPlatform?.transformFailed) {
    issues.push(
      issue(
        'media_transform_failed',
        'The platform media transform failed; publish would fall back to the source asset.',
        'blocked',
        'transform',
      ),
    );
  }

  const blockingIssues = issues.filter((item) => item.severity === 'blocked');
  const warningIssues = issues.filter((item) => item.severity === 'warning');
  const state: PostingReadinessState =
    lifecycle === 'retrying'
      ? 'retrying'
      : blockingIssues.length > 0
        ? 'blocked'
        : warningIssues.length > 0
          ? 'warning'
          : 'ready';
  const canPublish = state === 'ready' || state === 'warning';
  const result = { platform, state, canPublish, mediaType, issues, blockingIssues, warningIssues };
  return { ...result, primaryAction: primaryActionFor(result) };
}

export function summarizeReadiness(results: PostingReadinessResult[]): PostingReadinessState {
  if (results.some((result) => result.state === 'blocked')) return 'blocked';
  if (results.some((result) => result.state === 'terminal_failed')) return 'terminal_failed';
  if (results.some((result) => result.state === 'retrying')) return 'retrying';
  if (results.some((result) => result.state === 'warning')) return 'warning';
  if (results.every((result) => result.state === 'locked_live')) return 'locked_live';
  if (results.some((result) => result.state === 'paused')) return 'paused';
  return 'ready';
}
